/**
 * TransactionCard — the ONE way to render a transaction card.
 *
 * Usage:
 *   <TransactionCard transactionId={tx.id.uuid} variant="renter-upcoming" />
 *
 * Variants supported (see design doc):
 *   - renter-upcoming    (RenterDashboardPage Upcoming Rentals)
 *   - host-upcoming      (BookingsPage Pending + Upcoming)
 *   - host-past          (BookingsPage Past)
 *   - host-attention-row (ProviderDashboardPage Needs Your Attention)
 *
 * Null-safety contract (from useTransactionCardData):
 *   - tx entity missing from marketplaceData → null
 *   - relationship (listing/provider/customer/booking/image) not yet
 *     resolved (race with in-flight query) → null
 *   - listing deleted → null
 *   - tx process state unresolvable → null
 *   - unknown variant → null + console.warn
 *
 * React renders null as nothing; parent lists should rely on the
 * shared filterCardTxs() helper for empty-state counts rather than
 * counting rendered children.
 *
 * The card is always a <NamedLink> — clicking navigates to the
 * appropriate detail page. Route is auto-selected from variant
 * (renter-* → OrderDetailsPage, host-* → SaleDetailsPage) unless
 * overridden via `linkRouteName`.
 */
import React from 'react';

// Import directly, not via the components barrel — TransactionCard is
// re-exported from that barrel, so going through it creates a
// circular dependency at module-eval time.
import NamedLink from '../NamedLink/NamedLink';

import { useTransactionCardData } from './useTransactionCardData';
import RenterUpcomingLayout from './variants/RenterUpcomingLayout';
import HostUpcomingLayout from './variants/HostUpcomingLayout';
import HostPastLayout from './variants/HostPastLayout';
import HostAttentionRowLayout from './variants/HostAttentionRowLayout';

const LAYOUTS = {
  'renter-upcoming': RenterUpcomingLayout,
  'host-upcoming': HostUpcomingLayout,
  'host-past': HostPastLayout,
  'host-attention-row': HostAttentionRowLayout,
};

const ROUTE_FOR_VARIANT = {
  'renter-upcoming': 'OrderDetailsPage',
  'host-upcoming': 'SaleDetailsPage',
  'host-past': 'SaleDetailsPage',
  'host-attention-row': 'SaleDetailsPage',
};

const TransactionCard = ({
  transactionId,
  variant,
  linkRouteName,
  className,
}) => {
  const data = useTransactionCardData(transactionId);
  if (!data) return null;

  const Layout = LAYOUTS[variant];
  if (!Layout) {
    // eslint-disable-next-line no-console
    console.warn(`TransactionCard: unknown variant "${variant}"`);
    return null;
  }

  const routeName = linkRouteName || ROUTE_FOR_VARIANT[variant];

  return (
    <NamedLink
      name={routeName}
      params={{ id: transactionId }}
      className={className}
    >
      <Layout data={data} />
    </NamedLink>
  );
};

export default TransactionCard;
